/**
 * Title screen — the game's entry gate, shown before anything boots. A full-
 * screen DOM overlay (no PixiJS) styled to match the neon/CRT ASCII UI.
 *
 * Three routes:
 *  - START TUTORIAL → boots the current hand-built test floor (calls `onStart`,
 *    which hides this and runs main()).
 *  - NEW GAME → the planned procedural extraction mode; a "coming soon" panel
 *    for now (the dungeon generator is being designed next).
 *  - SETTINGS → a small settings panel (CRT / screen-shake toggles, persisted to
 *    localStorage via src/settings.ts) plus a controls reference.
 *
 * Keyboard: ↑/↓ move the selection, Enter activates, Esc backs out of a sub-view.
 * The keydown listener is removed on hide so it never fights the in-game input.
 */

import { buildColor, buildLabel } from '../build';
import { loadSettings, saveSettings, type GameSettings } from '../settings';

type View = 'menu' | 'newgame' | 'settings';

/** Parameters collected on the NEW GAME (mission) screen, passed to boot a run. */
export interface MissionParams {
  budget: number; // 1..5 ($–$$$$$)
  type: string; // objective family (biases rooms; quest logic is a later phase)
  timeOfDay: 'in-use' | 'off-duty';
  seed: number;
}

export interface TitleScreenCallbacks {
  onStartTutorial: () => void;
  onNewGame: (mission: MissionParams) => void;
}

const CONTROLS: Array<[string, string]> = [
  ['WASD', 'move (camera-relative)'],
  ['Shift', 'run'],
  ['Ctrl / C', 'crouch'],
  ['← ↑ → ↓', 'look / aim'],
  ['F', 'focus lock'],
  ['G / V', 'cycle target'],
  ['1 / 2 / 3', 'weapon'],
  ['Space', 'fire'],
  ['E', 'interact'],
  ['N', 'night vision'],
  ['I', 'inventory'],
  ['`', 'debug overlay'],
];

export class TitleScreen {
  private readonly overlay: HTMLElement;
  private view: View = 'menu';
  private activeIdx = 0;
  private readonly onKeyBound: (e: KeyboardEvent) => void;

  constructor(private readonly cb: TitleScreenCallbacks) {
    const el = document.createElement('div');
    el.id = 'title-screen';
    el.className = 'ts-overlay';
    el.innerHTML = this.markup();
    this.overlay = el;
    this.onKeyBound = (e) => this.onKey(e);

    el.addEventListener('click', (e) => this.onClick(e));
    el.addEventListener('change', (e) => this.onChange(e));
    // Hover selects, so mouse + keyboard highlight stay in sync.
    el.addEventListener('pointermove', (e) => {
      const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.ts-btn');
      if (btn) this.setActive(this.buttons().indexOf(btn));
    });
  }

  show(): void {
    document.body.appendChild(this.overlay);
    window.addEventListener('keydown', this.onKeyBound);
    this.setView('menu');
  }

  hide(): void {
    window.removeEventListener('keydown', this.onKeyBound);
    this.overlay.remove();
  }

  // ---- rendering ----

  private markup(): string {
    return `
      <div class="ts-scanlines" aria-hidden="true"></div>
      <div class="ts-center">
        <div class="ts-logo">CHAGAT<span class="ts-logo-dim">.ai</span></div>
        <div class="ts-tag">STEALTH&nbsp; EXTRACTION&nbsp; · &nbsp;YEAR&nbsp; 400,000</div>
        <div class="ts-panel">
          <div class="ts-panel-title">MAIN MENU</div>
          <div class="ts-body"></div>
        </div>
        <div class="ts-foot">vertical slice · a rogue bandit raids the towers</div>
        <div class="ts-build" style="color:${buildColor()}">${buildLabel()}</div>
      </div>`;
  }

  private setView(v: View): void {
    this.view = v;
    this.activeIdx = 0;
    const body = this.overlay.querySelector('.ts-body') as HTMLElement;
    const title = this.overlay.querySelector('.ts-panel-title') as HTMLElement;
    if (v === 'menu') {
      title.textContent = 'MAIN MENU';
      body.innerHTML = this.menuMarkup();
    } else if (v === 'newgame') {
      title.textContent = 'NEW GAME';
      body.innerHTML = this.newGameMarkup();
    } else {
      title.textContent = 'SETTINGS';
      body.innerHTML = this.settingsMarkup(loadSettings());
    }
    this.setActive(0);
  }

  private menuMarkup(): string {
    return `
      <button class="ts-btn" data-act="tutorial">
        <span class="ts-btn-label">▸ START TUTORIAL</span>
        <span class="ts-btn-sub">the hand-built test floor</span>
      </button>
      <button class="ts-btn" data-act="newgame">
        <span class="ts-btn-label">▸ NEW GAME</span>
        <span class="ts-btn-sub">procedural extraction — in development</span>
      </button>
      <button class="ts-btn" data-act="settings">
        <span class="ts-btn-label">▸ SETTINGS</span>
      </button>`;
  }

  private newGameMarkup(): string {
    const seed = Math.floor(Math.random() * 100000);
    return `
      <div class="ts-sub-head">DEPLOY A MISSION</div>
      <label class="ms-row"><span class="ms-label">Budget</span>
        <select class="ms-input" data-ms="budget">
          <option value="1">$ · dirt poor</option>
          <option value="2">$$ · struggling</option>
          <option value="3" selected>$$$ · getting by</option>
          <option value="4">$$$$ · loaded</option>
          <option value="5">$$$$$ · filthy rich</option>
        </select></label>
      <label class="ms-row"><span class="ms-label">Objective</span>
        <select class="ms-input" data-ms="type">
          <option value="random" selected>Random</option>
          <option value="assassination">Assassination</option>
          <option value="heist">Heist</option>
          <option value="data-theft">Data theft</option>
          <option value="recon">Recon</option>
          <option value="extraction">Extraction</option>
          <option value="combined">Combined</option>
        </select></label>
      <label class="ms-row"><span class="ms-label">Time</span>
        <select class="ms-input" data-ms="time">
          <option value="in-use" selected>Day · in use</option>
          <option value="off-duty">Night · off duty</option>
        </select></label>
      <label class="ms-row"><span class="ms-label">Seed</span>
        <input class="ms-input ms-seed" data-ms="seed" type="number" value="${seed}" />
        <button class="ms-roll" data-act="roll" type="button" title="roll a new seed">⟳</button></label>
      <button class="ts-btn" data-act="deploy">
        <span class="ts-btn-label">▸ DEPLOY</span>
        <span class="ts-btn-sub">generate the building &amp; drop in</span></button>
      <button class="ts-btn ts-back" data-act="back"><span class="ts-btn-label">◂ BACK</span></button>`;
  }

  private settingsMarkup(s: GameSettings): string {
    const toggle = (key: keyof GameSettings, label: string, on: boolean) => `
      <label class="ts-setting">
        <input type="checkbox" data-set="${key}"${on ? ' checked' : ''} />
        <span class="ts-setting-box" aria-hidden="true"></span>
        <span class="ts-setting-label">${label}</span>
      </label>`;
    const rows = CONTROLS.map(
      ([k, a]) => `<div class="ts-ctl-key">${k}</div><div class="ts-ctl-act">${a}</div>`,
    ).join('');
    return `
      ${toggle('crt', 'CRT scanlines', s.crt)}
      ${toggle('screenShake', 'Screen shake', s.screenShake)}
      <div class="ts-note-sm">Applied when the game starts.</div>
      <div class="ts-controls">
        <div class="ts-ctl-title">CONTROLS</div>
        <div class="ts-ctl-grid">${rows}</div>
      </div>
      <button class="ts-btn ts-back" data-act="back"><span class="ts-btn-label">◂ BACK</span></button>`;
  }

  // ---- selection ----

  private buttons(): HTMLButtonElement[] {
    return [...this.overlay.querySelectorAll<HTMLButtonElement>('.ts-btn')];
  }

  private setActive(i: number): void {
    const btns = this.buttons();
    if (btns.length === 0) return;
    this.activeIdx = ((i % btns.length) + btns.length) % btns.length;
    btns.forEach((b, k) => b.classList.toggle('ts-active', k === this.activeIdx));
  }

  // ---- events ----

  private onClick(e: MouseEvent): void {
    const el = (e.target as HTMLElement).closest<HTMLElement>('[data-act]');
    if (el) this.activate(el.dataset.act ?? '');
  }

  private activate(act: string): void {
    switch (act) {
      case 'tutorial':
        this.cb.onStartTutorial();
        break;
      case 'newgame':
        this.setView('newgame');
        break;
      case 'settings':
        this.setView('settings');
        break;
      case 'back':
        this.setView('menu');
        break;
      case 'roll': {
        const seed = this.overlay.querySelector<HTMLInputElement>('[data-ms="seed"]');
        if (seed) seed.value = String(Math.floor(Math.random() * 100000));
        break;
      }
      case 'deploy':
        this.cb.onNewGame(this.readMission());
        break;
    }
  }

  private readMission(): MissionParams {
    const val = (ms: string): string =>
      this.overlay.querySelector<HTMLSelectElement | HTMLInputElement>(`[data-ms="${ms}"]`)?.value ?? '';
    return {
      budget: Number(val('budget')) || 3,
      type: val('type') || 'random',
      timeOfDay: val('time') === 'off-duty' ? 'off-duty' : 'in-use',
      seed: Number(val('seed')) || 1,
    };
  }

  private onChange(e: Event): void {
    const input = e.target as HTMLInputElement;
    const key = input.dataset.set as keyof GameSettings | undefined;
    if (!key) return;
    saveSettings({ [key]: input.checked } as Partial<GameSettings>);
  }

  private onKey(e: KeyboardEvent): void {
    switch (e.code) {
      case 'ArrowDown':
        e.preventDefault();
        this.setActive(this.activeIdx + 1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        this.setActive(this.activeIdx - 1);
        break;
      case 'Enter':
      case 'Space': {
        e.preventDefault();
        const btn = this.buttons()[this.activeIdx];
        if (btn) this.activate(btn.dataset.act ?? '');
        break;
      }
      case 'Escape':
        if (this.view !== 'menu') {
          e.preventDefault();
          this.setView('menu');
        }
        break;
    }
  }
}
